import React, { useState } from "react";
import { ServicePageHero, FeatureGrid, ProcessSteps, CTAStripe } from "@/components/sections/Shared";
import QuoteDialog from "@/components/QuoteDialog";
import { IMAGES } from "@/lib/business";
import { ShieldCheck, Sun, Eye, Palette, Sparkles, Home } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import SEO, { serviceJsonLd } from "@/components/SEO";

const FEATURES = [
    {
        icon: ShieldCheck,
        title: "Safety Security Film",
        description:
            "A tough, clear film that holds glass together under impact — deterring break-ins and protecting against shatter.",
    },
    {
        icon: Sun,
        title: "Solar Control",
        description:
            "Reduce heat gain and glare while blocking up to 99% of harmful UV rays that fade your furnishings.",
    },
    {
        icon: Eye,
        title: "Privacy Film",
        description:
            "Frosted, tinted, or mirrored finishes give daytime privacy while keeping rooms bright and inviting.",
    },
    {
        icon: Palette,
        title: "Decorative Film",
        description:
            "Elegant decorative and frosted patterns that transform ordinary glass into a designer feature.",
    },
    {
        icon: Sparkles,
        title: "Anti-Graffiti Film",
        description:
            "Sacrificial film that protects glass surfaces in high-traffic areas — simply replace instead of replacing the glass.",
    },
    {
        icon: Home,
        title: "Full-Home Packages",
        description:
            "Bundled installs for entire homes — consistent performance and finish on every window.",
    },
];

const FAQS = [
    {
        q: "Will the film make my windows look dark?",
        a: "Safety and solar films are available in clear or lightly tinted finishes. Your view and natural light stay intact. We'll walk you through the options at the quote.",
    },
    {
        q: "Does security film really help against break-ins?",
        a: "Yes — impact-resistant film keeps glass in the frame even if it cracks, making forced entry significantly slower and louder, which deters most intruders.",
    },
    {
        q: "Can film be applied to existing windows?",
        a: "Absolutely. We install onto your existing glass with no frame or glazing changes needed. It's one of the fastest upgrades for safety and comfort.",
    },
    {
        q: "How long does it last?",
        a: "Premium residential films typically carry manufacturer warranties of 10-15 years when professionally installed. We stand behind our workmanship.",
    },
];

export default function SecurityFilmsResidential() {
    const [quoteOpen, setQuoteOpen] = useState(false);
    return (
        <>
            <SEO
                title="Residential Security Window Films — GTA Install"
                description="Protect your home with professionally installed security, solar, privacy and decorative window films. Free on-site quote across the Greater Toronto Area."
                path="/security-films/residential"
                keywords="residential window film GTA, home security film, solar window film Toronto, privacy window film, UV protection film"
                jsonLd={serviceJsonLd(
                    "Residential Window Films",
                    "Professionally installed security, solar, privacy, and decorative window films for homes in the Greater Toronto Area.",
                )}
            />
            <ServicePageHero
                eyebrow="Residential Window Films"
                title="Stronger, safer, more comfortable glass."
                subtitle="Protect your family and home with clear, professionally-installed window films — from safety and solar to privacy and decorative finishes."
                image="https://customer-assets.emergentagent.com/job_gtasecure-staging/artifacts/iw4sjun5_windows%20securit%20film%202.png"
                defaultQuoteService="Residential Window Films"
                bullets={[
                    "Helps deter break-ins and accidental shatter",
                    "Blocks up to 99% of harmful UV rays",
                    "Reduces heat gain and glare in sunny rooms",
                    "Optional privacy and decorative finishes",
                ]}
            />

            <section className="section bg-white">
                <div className="container-x">
                    <div className="max-w-2xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> Residential film options
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            The right film for every room.
                        </h2>
                    </div>
                    <div className="mt-12">
                        <FeatureGrid items={FEATURES} />
                    </div>
                </div>
            </section>

            <section className="section bg-[#FDFCFB]">
                <div className="container-x">
                    <div className="max-w-2xl">
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> Our install process
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Clean, precise, and efficient.
                        </h2>
                    </div>
                    <div className="mt-12">
                        <ProcessSteps
                            steps={[
                                { title: "On-site assessment", description: "We measure, inspect, and recommend the best film for each window and room." },
                                { title: "Precision installation", description: "Our certified installers prepare, cut, and apply the film dust-free for a flawless finish." },
                                { title: "Cure & walkthrough", description: "We walk you through the cure time, care tips, and leave your home spotless." },
                            ]}
                        />
                    </div>
                </div>
            </section>

            <section className="section bg-white">
                <div className="container-x grid lg:grid-cols-[1fr_1.3fr] gap-12">
                    <div>
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" /> FAQ
                        </p>
                        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-navy leading-tight">
                            Residential film, explained.
                        </h2>
                    </div>
                    <Accordion type="single" collapsible className="w-full">
                        {FAQS.map((f, i) => (
                            <AccordionItem value={`r-${i}`} key={f.q} className="border-b border-gray-200">
                                <AccordionTrigger className="text-left font-semibold text-navy text-base md:text-lg py-5 hover:no-underline">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed pb-6">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            <CTAStripe
                heading="Ready to protect your home?"
                subheading="Free, transparent quote — usually within one business day."
                ctaLabel="Request Free Quote"
                onCta={() => setQuoteOpen(true)}
            />
            <QuoteDialog
                open={quoteOpen}
                onOpenChange={setQuoteOpen}
                defaultService="Residential Window Films"
            />
        </>
    );
}
